import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, ArrowLeft, Loader2, Check, Sparkles, X, Plus, Minus, Venus, Mars,
  Flame, Dumbbell, Activity, Heart, BatteryMedium, BatteryFull, Zap,
  Building2, Home, TreePine, Shuffle, Package, CalendarClock, HeartPulse, Salad, Rocket, PartyPopper,
} from 'lucide-react';
import { toast } from 'sonner';

/* Étapes structurées : synchronisées avec le profil unique (client_profiles/body_metrics),
   le MÊME que le Calculateur de Calories — sautées automatiquement si déjà connues. */
const STRUCTURED_STEPS = [
  {
    id: 'gender', target: 'profile', key: 'gender', kind: 'cards2',
    title: ['Quel est votre sexe ?', 'What is your gender?'],
    subtitle: ['Pour adapter précisément vos calculs métaboliques', 'To precisely tailor your metabolic calculations'],
    options: [
      { value: 'male', label: ['Homme', 'Man'], icon: Mars },
      { value: 'female', label: ['Femme', 'Woman'], icon: Venus },
    ],
  },
  {
    id: 'age', target: 'profile', key: 'age', kind: 'stepper',
    title: ['Quel âge avez-vous ?', 'How old are you?'],
    unit: ['ans', 'yo'], min: 15, max: 100, step: 1, default: 30,
  },
  {
    id: 'weight', target: 'profile', key: 'weight', kind: 'stepper',
    title: ['Votre poids actuel', 'Your current weight'],
    unit: ['kg', 'kg'], min: 30, max: 300, step: 0.5, default: 70,
  },
  {
    id: 'height', target: 'profile', key: 'height', kind: 'stepper',
    title: ['Votre taille', 'Your height'],
    unit: ['cm', 'cm'], min: 100, max: 250, step: 1, default: 170,
  },
  {
    id: 'goal', target: 'profile', key: 'goal', kind: 'cards1',
    title: ['Quel est votre objectif principal ?', 'What is your main goal?'],
    options: [
      { value: 'weight_loss', label: ['Perte de poids', 'Fat loss'], desc: ['Brûler du gras, affiner la silhouette', 'Burn fat, get leaner'], icon: Flame },
      { value: 'muscle_gain', label: ['Prise de muscle', 'Muscle gain'], desc: ['Gagner en force et volume', 'Gain strength and size'], icon: Dumbbell },
      { value: 'recomposition', label: ['Recomposition', 'Recomposition'], desc: ['Perdre du gras, gagner du muscle', 'Lose fat, build muscle'], icon: Activity },
      { value: 'maintenance', label: ['Maintien', 'Maintenance'], desc: ['Rester en forme et tonique', 'Stay fit and toned'], icon: Heart },
    ],
  },
  {
    id: 'level', target: 'profile', key: 'training_level', kind: 'cards1',
    title: ["Votre niveau d'entraînement", 'Your training level'],
    options: [
      { value: 'beginner', label: ['Débutant', 'Beginner'], desc: ['Je commence tout juste', "I'm just starting out"], icon: BatteryMedium },
      { value: 'intermediate', label: ['Intermédiaire', 'Intermediate'], desc: ["1 à 2 ans d'expérience", '1-2 years of experience'], icon: BatteryFull },
      { value: 'advanced', label: ['Avancé', 'Advanced'], desc: ['+ de 3 ans de pratique régulière', '3+ years of regular practice'], icon: Zap },
    ],
  },
];

/* Étapes qualitatives : spécifiques au coaching (jamais demandées par le Calculateur). */
const QUALITATIVE_STEPS = [
  {
    id: 'lieu', target: 'answers', key: 'lieu', kind: 'cards2',
    title: ["Où vous entraînez-vous ?", 'Where do you train?'],
    options: [
      { value: 'Salle de sport', label: ['Salle de sport', 'Salle de sport'], icon: Building2 },
      { value: 'À la maison', label: ['À la maison', 'À la maison'], icon: Home },
      { value: 'En extérieur', label: ['En extérieur', 'En extérieur'], icon: TreePine },
      { value: 'Mixte', label: ['Mixte', 'Mixte'], icon: Shuffle },
    ],
  },
  { id: 'materiel', target: 'answers', key: 'materiel', kind: 'text', icon: Package, title: ['Matériel disponible', 'Available equipment'], placeholder: ['Haltères, élastiques, aucun...', 'Dumbbells, bands, none...'] },
  { id: 'disponibilites', target: 'answers', key: 'disponibilites', kind: 'text', icon: CalendarClock, title: ['Vos disponibilités', 'Your availability'], placeholder: ['Ex : lundi/mercredi/vendredi soir', 'Ex: Mon/Wed/Fri evening'] },
  { id: 'blessures', target: 'answers', key: 'blessures', kind: 'textarea', icon: HeartPulse, title: ['Blessures ou limitations ?', 'Any injuries or limitations?'], placeholder: ['Facultatif — précisez si besoin', 'Optional — specify if needed'] },
  {
    id: 'alimentation', target: 'answers', key: 'alimentation', kind: 'textarea', icon: Salad,
    title: ['Préférences ou allergies alimentaires', 'Food preferences or allergies'],
    placeholder: ['Facultatif — précisez si besoin', 'Optional — specify if needed'],
    skipIf: (p) => !!(p?.diet_preferences || p?.allergies),
  },
  {
    id: 'motivation', target: 'answers', key: 'motivation', kind: 'textarea', icon: Rocket,
    title: ['Quelle est votre motivation profonde ?', 'What is your deeper motivation?'],
    subtitle: ['Y a-t-il un événement précis ou une échéance ?', 'Is there a specific event or deadline?'],
    placeholder: ['Ex : retrouver la forme pour mon mariage en juillet...', 'Ex: get in shape for my wedding in July...'],
  },
];

const buildSteps = (profile) => {
  const structured = STRUCTURED_STEPS.filter((s) => !profile || profile[s.key] === undefined || profile[s.key] === null || profile[s.key] === '');
  const qualitative = QUALITATIVE_STEPS.filter((s) => !(s.skipIf && s.skipIf(profile)));
  return [...structured, ...qualitative];
};

const NumberStepper = ({ value, onChange, unit, min, max, step, testId }) => {
  const clamp = (v) => Math.min(max, Math.max(min, Math.round(v * 10) / 10));
  return (
    <div className="flex items-center justify-center gap-6 sm:gap-8 w-full my-8">
      <motion.button whileTap={{ scale: 0.9 }} onClick={() => onChange(clamp(value - step))}
        className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-white/[0.035] border border-white/10 backdrop-blur-xl flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white transition-colors shrink-0"
        data-testid={`${testId}-minus`}>
        <Minus className="w-5 h-5" />
      </motion.button>
      <div className="flex items-baseline gap-2 min-w-[130px] justify-center overflow-hidden">
        <AnimatePresence mode="popLayout">
          <motion.span key={value} initial={{ y: 18, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -18, opacity: 0 }}
            transition={{ duration: 0.18 }} className="text-6xl sm:text-7xl font-black tabular-nums tracking-tighter text-white drop-shadow-2xl"
            data-testid={`${testId}-value`}>
            {value}
          </motion.span>
        </AnimatePresence>
        <span className="text-xl sm:text-2xl font-light text-white/40">{unit}</span>
      </div>
      <motion.button whileTap={{ scale: 0.9 }} onClick={() => onChange(clamp(value + step))}
        className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-white/[0.035] border border-white/10 backdrop-blur-xl flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white transition-colors shrink-0"
        data-testid={`${testId}-plus`}>
        <Plus className="w-5 h-5" />
      </motion.button>
    </div>
  );
};

const CardOption = ({ active, onClick, icon: Icon, label, desc, testId, vertical }) => (
  <motion.button whileTap={{ scale: 0.97 }} onClick={onClick} data-testid={testId}
    className={`relative p-4 sm:p-5 rounded-2xl border backdrop-blur-xl flex ${vertical ? 'flex-row items-center gap-4 text-left' : 'flex-col items-center gap-3 text-center'} transition-colors duration-300 ${active ? 'border-fuchsia-500 bg-fuchsia-500/10 shadow-[0_0_20px_-5px_rgba(217,70,239,0.4)]' : 'border-white/10 bg-white/[0.035] hover:bg-white/[0.06]'}`}>
    {Icon && (
      <div className={`p-3 rounded-full shrink-0 transition-colors ${active ? 'bg-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/50' : 'bg-white/5 text-white/70'}`}>
        <Icon className="w-5 h-5" />
      </div>
    )}
    <div className="min-w-0">
      <p className="text-sm sm:text-base font-bold text-white">{label}</p>
      {desc && <p className="text-xs text-white/50 mt-0.5">{desc}</p>}
    </div>
    {active && <Check className="w-4 h-4 text-fuchsia-400 absolute top-3 right-3" />}
  </motion.button>
);

/* Onboarding "app-style" plein écran, une question à la fois. Les questions chiffrées
   (âge/poids/taille/genre/objectif/niveau) écrivent dans le MÊME profil que le Calculateur
   de Calories (`/api/tracking/profile`) et sont sautées automatiquement si déjà connues —
   jamais de redondance. Les questions coaching (lieu, matériel...) restent dans `/onboarding`. */
export const OnboardingWizard = ({ en, api, onDone, onSkip }) => {
  const [profile, setProfile] = useState(null);
  const [step, setStep] = useState(-1); // -1 = intro
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const L = useCallback((i) => i[en ? 1 : 0], [en]);

  useEffect(() => {
    api('/api/tracking/profile').then(setProfile).catch(() => setProfile({}));
  }, [api]);

  const steps = useMemo(() => (profile ? buildSteps(profile) : []), [profile]);

  useEffect(() => {
    if (!profile) return;
    const defaults = {};
    steps.forEach((s) => { if (s.kind === 'stepper') defaults[s.id] = s.default; });
    setAnswers((a) => ({ ...defaults, ...a }));
  }, [profile, steps]);

  if (profile === null) {
    return <div className="fixed inset-0 z-[70] bg-[#050505] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-fuchsia-400" /></div>;
  }

  const total = steps.length;
  const current = step >= 0 && step < total ? steps[step] : null;
  const value = current ? answers[current.id] : undefined;
  const canNext = !current || current.kind === 'textarea' || current.kind === 'stepper'
    || (current.kind === 'text' ? String(value || '').trim().length > 0 : value !== undefined && value !== '');

  const finish = async () => {
    setBusy(true);
    try {
      const profilePatch = {};
      const qualPatch = {};
      steps.forEach((s) => {
        if (answers[s.id] === undefined) return;
        if (s.target === 'profile') profilePatch[s.key] = answers[s.id];
        else qualPatch[s.key] = answers[s.id];
      });
      if (Object.keys(profilePatch).length) {
        await api('/api/tracking/profile', { method: 'PUT', body: JSON.stringify(profilePatch) });
      }
      await api('/api/tracking/onboarding', { method: 'POST', body: JSON.stringify({ answers: qualPatch }) });
      onDone && onDone();
    } catch {
      toast.error(en ? 'Save failed, please retry' : "Échec de l'enregistrement, réessayez");
      setBusy(false);
    }
  };

  const next = () => setStep((s) => Math.min(total, s + 1));
  const prev = () => setStep((s) => Math.max(-1, s - 1));
  const set = (v) => setAnswers((a) => ({ ...a, [current.id]: v }));

  return (
    <div className="fixed inset-0 z-[70] bg-[#050505] flex flex-col h-[100dvh] overflow-hidden" data-testid="onboarding-wizard">
      <motion.div className="absolute -top-1/3 -left-1/3 w-[150vw] h-[150vw] sm:w-[60vw] sm:h-[60vw] rounded-full bg-fuchsia-600/20 blur-[120px] pointer-events-none"
        animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.8, 0.5] }} transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }} />
      <motion.div className="absolute bottom-[-20%] right-[-20%] w-[120vw] h-[120vw] sm:w-[45vw] sm:h-[45vw] rounded-full bg-pink-600/10 blur-[120px] pointer-events-none"
        animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }} transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut', delay: 2 }} />

      <div className="absolute top-0 w-full px-5 sm:px-6 py-5 sm:py-6 flex justify-between items-center z-50">
        <span className="text-xs font-mono text-white/40 uppercase tracking-widest">
          {step < 0 ? (en ? 'Welcome' : 'Bienvenue') : step >= total ? (en ? 'Done' : 'Terminé') : `${step + 1} / ${total}`}
        </span>
        <button onClick={onSkip} className="inline-flex items-center gap-1.5 text-xs font-medium text-white/50 hover:text-white transition-colors px-3 py-1.5 rounded-full hover:bg-white/5" data-testid="onboarding-wizard-skip-btn">
          {en ? 'Skip for now' : "Passer pour l'instant"} <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 pt-24 pb-36 w-full max-w-2xl mx-auto overflow-y-auto">
        <AnimatePresence mode="wait">
          {step < 0 ? (
            <motion.div key="intro" initial={{ opacity: 0, x: 30, scale: 0.98 }} animate={{ opacity: 1, x: 0, scale: 1 }} exit={{ opacity: 0, x: -30, scale: 0.98 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }} className="text-center" data-testid="onboarding-wizard-intro">
              <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-fuchsia-600 to-pink-500 flex items-center justify-center mx-auto mb-6 shadow-[0_8px_30px_-10px_rgba(217,70,239,0.6)]">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {en ? 'Welcome to Body-Up 👋' : 'Bienvenue chez Body-Up 👋'}
              </h1>
              <p className="text-base sm:text-lg text-white/50 font-light leading-relaxed">
                {total > 0
                  ? (en ? `${total} quick questions so Laurent can build a program that truly fits you — about 2 minutes.` : `${total} questions rapides pour que Laurent te construise un programme qui te correspond vraiment — environ 2 minutes.`)
                  : (en ? 'Your profile is already complete — just confirm to continue.' : 'Ton profil est déjà complet — confirme simplement pour continuer.')}
              </p>
            </motion.div>
          ) : step >= total ? (
            <motion.div key="finish" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: 'spring', damping: 20 }}
              className="w-full text-center" data-testid="onboarding-wizard-finish">
              <motion.div initial={{ rotate: -15, scale: 0 }} animate={{ rotate: 0, scale: 1 }} transition={{ type: 'spring', damping: 12, delay: 0.1 }}
                className="w-16 h-16 rounded-3xl bg-gradient-to-br from-fuchsia-600 to-pink-500 flex items-center justify-center mx-auto mb-5 shadow-[0_8px_30px_-10px_rgba(217,70,239,0.6)]">
                <PartyPopper className="w-8 h-8 text-white" />
              </motion.div>
              <h1 className="text-2xl sm:text-3xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {en ? 'Profile complete' : 'Profil complété'}
              </h1>
              {(() => {
                const resolvedGoal = answers.goal || profile?.goal;
                const goalOption = STRUCTURED_STEPS.find((s) => s.id === 'goal').options.find((o) => o.value === resolvedGoal);
                if (!goalOption) return null;
                return (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30 text-fuchsia-300 text-sm font-semibold mb-4" data-testid="onboarding-finish-goal-chip">
                    <goalOption.icon className="w-4 h-4" /> {en ? 'Goal: ' : 'Objectif : '}{L(goalOption.label)}
                  </motion.div>
                );
              })()}
              <p className="text-white/50 mb-6">{en ? 'We are ready to build your custom program.' : 'Nous avons préparé votre suivi sur-mesure.'}</p>
              {steps.length > 0 && (
                <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
                  className="rounded-2xl bg-white/[0.035] border border-white/10 backdrop-blur-2xl p-4 space-y-2 text-left max-h-[30vh] overflow-y-auto" data-testid="onboarding-wizard-summary">
                  {steps.filter((s) => answers[s.id] !== undefined && answers[s.id] !== '').map((s) => {
                    const opt = s.options?.find((o) => o.value === answers[s.id]);
                    const display = s.kind === 'stepper' ? `${answers[s.id]} ${L(s.unit)}` : opt ? L(opt.label) : String(answers[s.id]).slice(0, 60);
                    return (
                      <motion.div key={s.id} variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                        className="flex items-center justify-between gap-3 py-1.5 border-b border-white/5 last:border-0">
                        <span className="text-xs uppercase tracking-wider text-white/40">{L(s.title)}</span>
                        <span className="text-sm font-semibold text-white truncate max-w-[55%]">{display}</span>
                      </motion.div>
                    );
                  })}
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div key={current.id} initial={{ opacity: 0, x: 30, scale: 0.98 }} animate={{ opacity: 1, x: 0, scale: 1 }} exit={{ opacity: 0, x: -30, scale: 0.98 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }} className="w-full" data-testid={`onboarding-wizard-step-${current.id}`}>
              {current.icon && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-11 h-11 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4 text-fuchsia-400">
                  <current.icon className="w-5 h-5" />
                </motion.div>
              )}
              <motion.h2 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="text-xl sm:text-2xl font-bold tracking-tight text-white/95 text-center mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {L(current.title)}
              </motion.h2>
              {current.subtitle && (
                <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-sm text-white/40 text-center mb-2">
                  {L(current.subtitle)}
                </motion.p>
              )}

              {current.kind === 'stepper' && (
                <NumberStepper value={value} onChange={set} unit={L(current.unit)} min={current.min} max={current.max} step={current.step} testId={`onboarding-stepper-${current.id}`} />
              )}

              {(current.kind === 'cards2' || current.kind === 'cards1') && (
                <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
                  className={`mt-6 grid gap-3 ${current.kind === 'cards2' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {current.options.map((o) => (
                    <motion.div key={o.value} variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { type: 'spring' } } }}>
                      <CardOption active={value === o.value} onClick={() => set(o.value)} icon={o.icon} label={L(o.label)} desc={o.desc && L(o.desc)}
                        vertical={current.kind === 'cards1'} testId={`onboarding-option-${current.id}-${o.value}`} />
                    </motion.div>
                  ))}
                </motion.div>
              )}

              {current.kind === 'text' && (
                <input autoFocus value={value || ''} onChange={(e) => set(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && canNext) next(); }}
                  placeholder={L(current.placeholder || ['', ''])}
                  className="w-full bg-transparent border-b-2 border-white/15 focus:border-fuchsia-500 text-xl sm:text-2xl font-semibold text-white text-center py-4 mt-4 focus:outline-none transition-colors placeholder:text-white/20"
                  data-testid={`onboarding-wizard-text-${current.id}`} />
              )}

              {current.kind === 'textarea' && (
                <textarea autoFocus rows={4} value={value || ''} onChange={(e) => set(e.target.value)}
                  placeholder={L(current.placeholder || ['', ''])}
                  className="w-full bg-white/[0.035] border border-white/10 focus:border-fuchsia-500 rounded-2xl px-5 py-4 mt-4 text-white text-base focus:outline-none transition-colors placeholder:text-white/20"
                  data-testid={`onboarding-wizard-textarea-${current.id}`} />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="absolute bottom-0 w-full p-5 sm:p-6 bg-gradient-to-t from-[#050505] via-[#050505]/90 to-transparent z-50 flex flex-col items-center gap-4">
        {total > 0 && (
          <div className="w-full max-w-sm flex gap-1 h-1.5">
            {steps.map((_, i) => (
              <div key={i} className={`flex-1 rounded-full transition-colors duration-500 ${i < step || step >= total ? 'bg-gradient-to-r from-fuchsia-600 to-pink-500' : i === step ? 'bg-white/30' : 'bg-white/10'}`} />
            ))}
          </div>
        )}
        <div className="w-full max-w-sm flex items-center justify-between gap-3">
          <button onClick={prev} disabled={step < 0 || busy}
            className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full text-sm font-medium text-white/50 hover:text-white disabled:opacity-0 transition-colors"
            data-testid="onboarding-wizard-prev-btn">
            <ArrowLeft className="w-4 h-4" /> {en ? 'Back' : 'Précédent'}
          </button>
          <button onClick={step >= total ? finish : next} disabled={!canNext || busy}
            className="flex-1 h-14 rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 text-white font-bold text-base shadow-[0_8px_30px_-10px_rgba(217,70,239,0.5)] hover:shadow-[0_10px_40px_-10px_rgba(217,70,239,0.7)] hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2 max-w-[220px]"
            data-testid={step >= total ? 'onboarding-wizard-finish-btn' : 'onboarding-wizard-next-btn'}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : step >= total ? <Check className="w-4 h-4" /> : null}
            {step < 0 ? (en ? "Let's go" : "C'est parti") : step >= total ? (en ? "Let's start" : "C'est parti") : (en ? 'Next' : 'Suivant')}
            {step < total && !busy && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
